import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { readSessionCookie } from '@/shared/utils/cookies';
import { ApiError } from './api.error';

/**
 * Wires request + response interceptors onto the shared axios instance.
 *
 * Request side (does two things every call):
 *   1. **Tenant header** — reads `ps_dev_session` from `document.cookie` and
 *      writes `x-tenant-slug`. The API uses this to scope every dashboard
 *      query (legacy app did it server-side from the session).
 *   2. **Bearer token** — placeholder. Today the API runs in dev-mode and
 *      skips bearer auth. When Clerk-react ships, an auth provider can call
 *      `setBearerProvider(...)` to install a function this hook calls.
 *
 * Response side (single funnel for all failures):
 *   - Normalize every error into `ApiError` so call sites get one shape.
 *   - **No automatic sign-out on 401.** In dev mode the session lives in the
 *     local cookie, not in API state — many dashboard endpoints currently
 *     return 401 even with a valid session (the API's dev-mode fallback
 *     hasn't shipped for those routes yet). Auto-redirecting on 401 caused
 *     a hard `window.location.assign` reload that looked like the page
 *     was refreshing on every link click. Components handle their own
 *     error UI; sign-out is an explicit user action via `useSignOut`.
 *
 * Refresh token (Clerk path):
 *   - When Clerk-react ships, register a callback via `setOnUnauthorized(...)`
 *     that calls Clerk's signOut + redirects. Until then we don't react to
 *     401s — the cookie is the source of truth.
 */

let bearerProvider: (() => Promise<string | null>) | null = null;
let onUnauthorized: (() => void) | null = null;

/** Installed by an auth provider once Clerk-react (or equivalent) is wired. */
export function setBearerProvider(provider: (() => Promise<string | null>) | null): void {
    bearerProvider = provider;
}

/** Installed by an auth provider that wants to handle 401 globally (e.g.
 *  Clerk-react would call Clerk.signOut() then navigate). Left unset by
 *  default so the interceptor doesn't make sign-out decisions on its own. */
export function setOnUnauthorized(handler: (() => void) | null): void {
    onUnauthorized = handler;
}

export function registerInterceptors(client: AxiosInstance): void {
    client.interceptors.request.use(async (config) => attachAuthAndTenant(config));

    client.interceptors.response.use(
        (res) => res,
        (err) => {
            const apiErr = ApiError.from(err);
            if (apiErr.status === 401 && onUnauthorized) onUnauthorized();
            return Promise.reject(apiErr);
        },
    );
}

async function attachAuthAndTenant(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
    const session = readSessionCookie();
    config.headers = config.headers ?? {};
    if (session?.tenantSlug) {
        config.headers.set?.('x-tenant-slug', session.tenantSlug);
    }
    // Only fall back to the stored bearer when the caller didn't set one
    // explicitly. Admin and tenant requests now share the same session bearer
    // (`ps_auth_token`); the `role` claim inside it is what the API authorizes on.
    const hasExplicitAuth = config.headers.has?.('authorization') ?? false;
    if (bearerProvider && !hasExplicitAuth) {
        const token = await bearerProvider();
        if (token) config.headers.set?.('authorization', `Bearer ${token}`);
    }
    return config;
}
